import * as profileAPI from "app/api/profileApi";
import { CheckboxWithLabel } from "app/components/Checkbox/CheckboxWithLabel";
import React, { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import PreferencesMessageError from "../PreferencesMessageError/PreferencesMessageError";
import PreferencesMessageSuccess from "../PreferencesMessageSuccess/PreferencesMessageSuccess";

const PreferencesOddsSelector = ({
  OddsType,
  account,
  intl,
}): React.JSX.Element => {
  const [oddsType, setOddsType] = useState(account.preferences.oddsType);
  const [consentToMarketing, setConsentToMarketing] = useState(
    account.preferences.consentToMarketing || false
  );
  const [changePreferencesSuccess, setChangePreferencesSuccess] =
    useState<JSX.Element | null>(null);
  const [changePreferencesFailed, setChangePreferencesFailed] =
    useState<JSX.Element | null>(null);
  const [valueChanged, setValueChanged] = useState(false);

  const handleCheckboxToggle = useCallback(e => {
    setConsentToMarketing(e.currentTarget.checked);
    setValueChanged(true);
  }, []);

  const onOddsTypeChange = useCallback(e => {
    setOddsType(e.target.value);
    setValueChanged(true);
  }, []);

  const validate = useCallback(() => {
    if (!oddsType) {
      setChangePreferencesFailed(
        <span className="preferences__message--error">
          <FormattedMessage tagName="span" id="settings-odds-types-not-set" />
        </span>
      );
      return false;
    }
    return true;
  }, [oddsType]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      if (!validate()) return;

      const data = {
        accountId: account.accountId,
        userId: account.userId,
        preferences: {
          oddsType,
          consentToMarketing,
        },
      };

      profileAPI
        .updateAccountDetails(data)
        .then(() => {
          setChangePreferencesSuccess(<PreferencesMessageSuccess />);
          setChangePreferencesFailed(null);
        })
        .catch(() => {
          setChangePreferencesFailed(<PreferencesMessageError />);
          setChangePreferencesSuccess(null);
        });
    },
    [oddsType, consentToMarketing, account, validate]
  );

  const disabled = !valueChanged;

  const oddsTypesTranslations = {
    US: intl.formatMessage({
      id: "oddstypes-US",
    }),
    DECIMAL: intl.formatMessage({
      id: "oddstypes-Decimal",
    }),
    HONG_KONG: intl.formatMessage({
      id: "oddstypes-Hong Kong",
    }),
    INDONESIAN: intl.formatMessage({
      id: "oddstypes-Indonesian",
    }),
    MALAY: intl.formatMessage({
      id: "oddstypes-Malay",
    }),
  };

  return (
    <div>
      <form className="preferences__form" onSubmit={onSubmit}>
        <div className="text__input--row">
          <label className="text-input__label" htmlFor="oddstype">
            <FormattedMessage tagName="span" id="settings-oddstype" />
          </label>
          <select
            className="form-control malay"
            value={oddsType}
            onChange={onOddsTypeChange}
            id="odds_type_dropdown"
          >
            {/* Options for different odds types */}
            {Object.entries(oddsTypesTranslations).map(([key, value]) => (
              <option key={key} value={OddsType[key]}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="text__input--row">
          <CheckboxWithLabel
            checked={consentToMarketing}
            onChange={handleCheckboxToggle}
            disabled={false}
            labelText={<FormattedMessage id="consent-to-marketing" />}
            id="consentToMarketing"
          />
        </div>
        <div className="preferences__buttons">
          <button
            type="submit"
            className="preferences__buttons__submit-button"
            disabled={disabled}
            id="liquidity_limit_submit_button"
          >
            <FormattedMessage tagName="span" id="settings-update-preferences" />
          </button>
        </div>
        <span className="preferences__message">
          {changePreferencesSuccess}
          {changePreferencesFailed}
        </span>
      </form>
    </div>
  );
};

export default PreferencesOddsSelector;
